import {observer} from 'mobx-react-lite';
import {PDFDocumentProxy} from 'pdfjs-dist';
import React, {
  ChangeEvent,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Document as PdfDocument,
  DocumentProps as PdfDocumentProps,
  Page as PdfPage,
} from 'react-pdf';
import {ETextInput, EUndecoratedButton} from 'webapp-lib/Components';
import {showSnackbar, SnackbarType} from 'webapp-lib/Functions';
import {classy, useDebouncedValue} from 'webapp-lib/utils';

import Loading from '#/Loading';
import {
  ChevronDoubleLeft,
  ChevronDoubleRight,
  ChevronLargeLeft,
  ChevronLargeRight,
  ChevronSmallLeft,
  ChevronSmallRight,
  Expand,
  Info,
} from '_/assets/images';
import {DOCUMENT_ICONS_MAP, LOCKED_DOCUMENT_ICONS_MAP} from '_/constants';
import {I18NextNS} from '_/i18n';
import {ArtifactTypeUnion} from '_/utilities/types';

import {PdfDocumentGuard} from './PdfDocumentGuard';
import {PdfHighlight} from './PdfHighlight.utils';
import {PdfHighlightList} from './PdfHighlightList/PdfHighlightList';
import {
  BottomPageNavigation,
  NavigationButton,
  PasswordProtectedInfo,
  PdfHighlightsWrapper,
  StyledPdfHighlightsColumn,
  StyledPdfViewer,
  StyledPdfViewerColumn,
} from './PdfViewer.styles';
import {
  isDestroyedPdfDocument,
  isDocumentUnavailableLoadError,
} from './PdfViewer.utils';
import {usePdfHighlights} from './usePdfHighlights';

export type PdfViewerProps = Omit<
  PdfDocumentProps,
  'className' | 'inputRef' | 'onItemClick' | 'onClick'
> & {
  width?: number;
  onLoadSuccess?: () => void;
  type?: ArtifactTypeUnion;
  onExpand?: () => void;
  initialPageNumber?: number;
  showHighlights?: boolean;
  onPageChange?: (newPageNumber: number) => void;
  initialActiveHighlight?: number;
  onHighlightChange?: (newHighlightIndex: number) => void;
};

const UPdfViewer = ({
  width,
  onLoadSuccess,
  onLoadError,
  error,
  loading,
  noData,
  type = 'UNKNOWN_DOCUMENT',
  onExpand,
  initialPageNumber,
  showHighlights = false,
  onPageChange,
  initialActiveHighlight,
  onHighlightChange,
  ...props
}: PdfViewerProps): React.ReactElement => {
  const {t} = useTranslation<I18NextNS>('common');
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState<{height: number; width: number}>();
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const {highlights, loadHighlights, resetHighlights} = usePdfHighlights();
  const pageRef = useRef<HTMLDivElement>(null);

  const [inputPageNumber, setInputPageNumber] = useState(1);

  function goToPageIndex(pageIndex: number) {
    setInputPageNumber(pageIndex + 1);

    if (isNaN(pageIndex)) {
      return;
    }

    if (pageIndex < 0) {
      setCurrentPageIndex(0);
    } else if (pageIndex >= numberOfPages) {
      setCurrentPageIndex(numberOfPages - 1);
    } else {
      setCurrentPageIndex(pageIndex);
    }

    onPageChange?.(pageIndex);
  }

  function navigateToFirstPage() {
    goToPageIndex(0);
  }

  function navigateToLastPage() {
    goToPageIndex(numberOfPages - 1);
  }

  function navigateToNextPage() {
    goToPageIndex(currentPageIndex + 1);
  }

  function navigateToPreviousPage() {
    goToPageIndex(currentPageIndex - 1);
  }

  const navigateToHighlight = useCallback(
    (item: PdfHighlight, highlightIndex: number) => {
      if (item) {
        goToPageIndex(item.pageIndex);
        setCurrentPageIndex(item.pageIndex);
        onPageChange?.(item.pageIndex);
        onHighlightChange?.(highlightIndex);
      }
    },
    // no need to trigger on goToPageNumber
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onPageChange, onHighlightChange]
  );

  const handleLoadSuccess = useCallback(
    (pdfDocument: PDFDocumentProxy) => {
      if (isDestroyedPdfDocument(pdfDocument)) {
        // the load of a file the viewer no longer shows, see PdfDocumentGuard
        return;
      }

      (async () => {
        resetHighlights();
        setNumberOfPages(pdfDocument.numPages);
        const initialPage = Math.min(
          initialPageNumber ?? 0,
          pdfDocument.numPages - 1
        );
        setCurrentPageIndex(initialPage);
        setInputPageNumber(initialPage + 1);
        setIsLoaded(true);
        if (showHighlights) {
          const pdfHighlights = await loadHighlights(pdfDocument);
          if (!pdfHighlights) {
            return;
          }
          if (initialPageNumber == null) {
            const highlightIndex = initialActiveHighlight ?? 0;
            navigateToHighlight(pdfHighlights[highlightIndex], highlightIndex);
          }
        }
        onLoadSuccess?.();
      })();
    },
    [
      initialPageNumber,
      showHighlights,
      onLoadSuccess,
      navigateToHighlight,
      initialActiveHighlight,
      loadHighlights,
      resetHighlights,
    ]
  );

  const [debouncedFile, file, setFile] = useDebouncedValue(props.file, {
    timeout: 500,
  });

  /* istanbul ignore next, This mechanism is used to overcome a problem in underlying pdf viewer library
  "react-pdf", the problem is when the source of the component changes rapidly it will crash the application,
   there is no value in testing it */
  useEffect(() => {
    if (props.file === debouncedFile) {
      return;
    }
    setNumberOfPages(0);
    setCurrentPageIndex(0);
    resetHighlights();
    setFile(props.file);
    // We explicitely only want to listen to changes of props.file
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.file]);

  useEffect(() => {
    if (!pageRef.current || !pageSize) {
      return;
    }

    pageRef.current.style.height = `${pageSize.height}px`;
    pageRef.current.style.width = `${pageSize.width}px`;
  }, [pageSize]);

  const isFirstPage = currentPageIndex === 0;
  const isLastPage = currentPageIndex === numberOfPages - 1;

  const viewer = (
    <>
      <StyledPdfViewer className='PdfViewer'>
        <div className='pdf-wrapper'>
          {onExpand ? (
            <EUndecoratedButton className='expand-button' onClick={onExpand}>
              <Expand />
            </EUndecoratedButton>
          ) : null}
          {numberOfPages > 0 && currentPageIndex > 0 ? (
            <NavigationButton
              className='prev-button'
              data-testid='prev-button'
              onClick={navigateToPreviousPage}
            >
              <ChevronLargeLeft />
            </NavigationButton>
          ) : null}
          <Suspense fallback={null}>
            <PdfDocument
              className={classy({
                loaded: isLoaded,
                isExpandable: Boolean(onExpand),
              })}
              {...props}
              error={
                isPasswordProtected
                  ? LOCKED_DOCUMENT_ICONS_MAP[type]
                  : error ?? DOCUMENT_ICONS_MAP[type]
              }
              file={file}
              loading={loading ?? <Loading />}
              noData={noData ?? <Loading />}
              onClick={() => onExpand?.()}
              onLoadError={(loadError) => {
                if (isDocumentUnavailableLoadError(loadError)) {
                  showSnackbar({
                    text: t('documentNotFoundCondition'),
                    type: SnackbarType.WARNING,
                    override: {
                      id: 'DocumentNotFoundCondition',
                      mode: 'REPLACE',
                    },
                  });
                }
                onLoadError?.(loadError);
              }}
              onLoadSuccess={handleLoadSuccess}
              onPassword={() => {
                setIsPasswordProtected(true);
                // throwing error is necessary to stop loading
                throw new Error('File is password protected');
              }}
            >
              <PdfDocumentGuard>
                <PdfPage
                  inputRef={pageRef}
                  pageIndex={currentPageIndex}
                  renderAnnotationLayer={true}
                  renderForms={true}
                  width={width}
                  onLoadSuccess={(page) => {
                    setPageSize({height: page.height, width: page.width});
                  }}
                />
              </PdfDocumentGuard>
            </PdfDocument>
          </Suspense>
          {numberOfPages > 0 && currentPageIndex + 1 < numberOfPages ? (
            <NavigationButton
              className='next-button'
              data-testid='next-button'
              onClick={navigateToNextPage}
            >
              <ChevronLargeRight />
            </NavigationButton>
          ) : null}
        </div>
        {numberOfPages > 1 ? (
          <BottomPageNavigation>
            <NavigationButton
              disabled={isFirstPage}
              onClick={navigateToFirstPage}
            >
              <ChevronDoubleLeft />
            </NavigationButton>
            <NavigationButton
              disabled={isFirstPage}
              onClick={navigateToPreviousPage}
            >
              <ChevronSmallLeft />
            </NavigationButton>
            <div className='page-number' data-testid='page-number'>
              <ETextInput
                max={numberOfPages}
                min={1}
                type='number'
                value={inputPageNumber.toString()}
                hideArrows
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  goToPageIndex(parseInt(e.target.value) - 1);
                }}
              />
              <span>
                {t('pdfViewer.pageIndex', {
                  number: numberOfPages,
                })}
              </span>
            </div>
            <NavigationButton
              disabled={isLastPage}
              onClick={navigateToNextPage}
            >
              <ChevronSmallRight />
            </NavigationButton>
            <NavigationButton
              disabled={isLastPage}
              onClick={navigateToLastPage}
            >
              <ChevronDoubleRight />
            </NavigationButton>
          </BottomPageNavigation>
        ) : null}
      </StyledPdfViewer>
      {isPasswordProtected ? (
        <PasswordProtectedInfo>
          <Info />
          <span>{t('pdfViewer.passwordProtected')}</span>
        </PasswordProtectedInfo>
      ) : null}
    </>
  );

  if (!showHighlights) {
    return viewer;
  }

  return (
    <PdfHighlightsWrapper>
      <StyledPdfHighlightsColumn>
        <PdfHighlightList
          highlights={highlights}
          initialActiveHighlight={initialActiveHighlight?.toString()}
          onActiveItemChange={(item, highlightIndex) =>
            item ? navigateToHighlight(item, highlightIndex) : null
          }
        />
      </StyledPdfHighlightsColumn>
      <StyledPdfViewerColumn>{viewer}</StyledPdfViewerColumn>
    </PdfHighlightsWrapper>
  );
};

export const PdfViewer = observer(UPdfViewer);
